<template>
  <div class="container">
      <h2>Alta de Orden de Compra</h2>

      <!-- Mostrar información del proveedor -->
      <div v-if="proveedor">
          <h4>Proveedor: {{ proveedor.nombre }}</h4>
      </div>

      <!-- Selección del estado de la orden de compra -->
      <div class="mb-3">
          <label for="estado" class="form-label">Estado de la Orden</label>
          <select id="estado" class="form-select" v-model="estadoSeleccionado">
              <option v-for="estado in estados" :key="estado" :value="estado">
                  {{ estado }}
              </option>
          </select>
      </div>

      <!-- Mostrar costo inicial estimado -->
      <div class="mb-3">
          <label for="costoInicialEstimado" class="form-label">Costo Inicial Estimado </label>
          <input
              type="number"
              id="costoInicialEstimado"
              class="form-control"
              v-model="costo_inicial_estimado_usd"
              disabled
          />
      </div>

      <!-- Lista de items seleccionados para la orden -->
      <div v-if="itemsSeleccionados.length > 0">
          <h3>Items Seleccionados</h3>
          <ul class="list-group mb-3">
              <li v-for="(item, index) in itemsSeleccionados" :key="item.id" class="list-group-item d-flex justify-content-between align-items-center">
                  {{ item.nombre }} - {{ item.cantidadOrdenada }} unidades - {{ item.costo_unitario_usd * item.cantidadOrdenada }} 
                  <button class="btn btn-danger btn-sm" @click="eliminarItem(index)">Eliminar</button>
              </li>
          </ul>

          <!-- Botón para confirmar la orden de compra -->
          <button class="btn btn-success" @click="confirmarOrden">Confirmar Orden de Compra</button>
      </div>

      <!-- Tabla para mostrar los items del proveedor -->
      <div v-if="items.length > 0">
          <table class="table table-striped">
              <thead>
                  <tr>
                      <th @click="ordenarPor('nombre')">Nombre del Ítem</th>
                      <th @click="ordenarPor('cantidad')">Cantidad Actual</th>
                      <th @click="ordenarPor('nivel_minimo')">Cantidad Mínima</th>
                      <th @click="ordenarPor('costo_unitario_usd')">Costo Unitario </th>
                      <th>Cantidad a Ordenar</th>
                      <th>Agregar</th>
                  </tr>
              </thead>
              <tbody>
                  <tr v-for="item in itemsOrdenados" :key="item.id">
                      <td>{{ item.nombre }}</td>
                      <td>{{ item.cantidad }}</td>
                      <td>{{ item.nivel_minimo }}</td>
                      <td>{{ item.costo_unitario_usd }}</td>
                      <td>
                          <input
                              type="number"
                              v-model.number="item.cantidadOrdenada"
                              class="form-control"
                              min="1"
                          />
                      </td>
                      <td>
                          <button
                              class="btn btn-primary"
                              @click="agregarItem(item)"
                              :disabled="item.cantidadOrdenada <= 0"
                          >
                              Agregar
                          </button>
                      </td>
                  </tr>
              </tbody>
          </table>
      </div>
  </div>
</template>

<script>
import axios from '@/axios';

export default {
  props: {
      proveedor_id: {
          type: [String, Number],
          required: true,
      },
  },
  data() {
      return {
          proveedor: null, // Información del proveedor
          items: [], // Items del proveedor
          itemsSeleccionados: [], // Lista de items seleccionados para la orden
          estadoSeleccionado: '', // Estado seleccionado de la orden
          estados: [], // Lista de estados de la API
          criterioOrden: 'nombre', // Criterio de ordenación
          ordenAscendente: true, // Controla el orden ascendente o descendente
          costo_inicial_estimado_usd: 0, // Inicializar el costo estimado
          clubActual: null,
      };
  },
  computed: {
      // Ordenar los items según el criterio seleccionado
      itemsOrdenados() {
          return [...this.items].sort((a, b) => {
              const factor = this.ordenAscendente ? 1 : -1;
              if (a[this.criterioOrden] < b[this.criterioOrden]) return -1 * factor;
              if (a[this.criterioOrden] > b[this.criterioOrden]) return 1 * factor;
              return 0;
          });
      },
  },
  methods: {
      async obtenerItemsProveedor() {
          try {
              const responseProveedor = await axios.get(`/api/proveedores/${this.proveedor_id}`, {
                  params: { club_id: this.clubActual.id },
              });
              this.proveedor = responseProveedor.data;

              const responseItems = await axios.get(`/api/inventario/${this.proveedor_id}`, {
                  params: { club_id: this.clubActual.id },
              });
              this.items = responseItems.data.map((item) => ({
                  ...item,
                  cantidadOrdenada: 0, // Campo adicional para la cantidad a ordenar
              }));
          } catch (error) {
              console.error('Error al obtener ítems del proveedor:', error);
          }
      },

      async obtenerEstados() {
          try {
              const response = await axios.get('/api/constantes/estados_orden_compra_inventario_items');
              this.estados = response.data;
          } catch (error) {
              console.error('Error al obtener estados de la orden de compra:', error);
          }
      },

      agregarItem(item) {
          const itemExistente = this.itemsSeleccionados.find((i) => i.id === item.id);
          if (!itemExistente) {
              this.itemsSeleccionados.push({ ...item });
          } else {
              itemExistente.cantidadOrdenada += item.cantidadOrdenada;
          }

          this.costo_inicial_estimado_usd += item.cantidadOrdenada * item.costo_unitario_usd;
          item.cantidadOrdenada = 0; // Reiniciar la cantidad después de agregar
      },

      eliminarItem(index) {
          const item = this.itemsSeleccionados[index];
          this.costo_inicial_estimado_usd -= item.cantidadOrdenada * item.costo_unitario_usd;
          this.itemsSeleccionados.splice(index, 1); // Eliminar el ítem de la lista
      },

      async confirmarOrden() {
          const nuevaOrden = {
              proveedor_id: this.proveedor_id,
              estado: this.estadoSeleccionado,
              costo_inicial_estimado_usd: this.costo_inicial_estimado_usd,
              items: this.itemsSeleccionados.map((item) => ({
                  inventario_item_id: item.id,
                  cantidad: item.cantidadOrdenada,
                  costo_unitario_usd: item.costo_unitario_usd,
              })),
          };

          try {
              if (nuevaOrden.items.length === 0) {
                  alert('No se han seleccionado items para la orden de compra.');
                  return;
              }
              if (nuevaOrden.estado === '') {
                  alert('No se ha seleccionado un estado para la orden de compra.');
                  return;
              }

              await axios.post('/api/ordenCompra', nuevaOrden);
              alert('Orden de compra creada con éxito.');
              this.itemsSeleccionados = []; // Limpiar la lista de ítems seleccionados
              this.costo_inicial_estimado_usd = 0; // Reiniciar el costo estimado

              this.$emit('ordenConfirmada', nuevaOrden); // Emitir evento
          } catch (error) {
              console.error('Error al crear la orden de compra:', error);
          }
      },

      ordenarPor(campo) {
          if (this.criterioOrden === campo) {
              this.ordenAscendente = !this.ordenAscendente;
          } else {
              this.criterioOrden = campo;
              this.ordenAscendente = true;
          }
      },
  },
  mounted() {
        this.clubActual=this.$store.getters.getClubActual;
      this.obtenerItemsProveedor();
      this.obtenerEstados();
  },
};
</script>

<style scoped>
.container {
  margin-top: 20px;
}

table {
  margin-top: 20px;
  cursor: pointer;
}
</style>
